// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — light or dark.
//
// A reading preference for this device, chosen in the account sheet and kept in
// localStorage beside the folded list groups. DOM-free: `ui_mobile_appearance.ts`
// applies the answer to the page.
// ─────────────────────────────────────────────────────────────────────────────

import { t } from "home/app/mobile/i18n";

/** What the user chose. `system` follows the phone's own setting, and keeps following it. */
export type Appearance = "light" | "dark" | "system";

/** The theme actually drawn. */
export type Theme = "light" | "dark";

/** The choices, in the order the account sheet offers them. */
export const APPEARANCES: readonly Appearance[] = ["light", "dark", "system"];

/**
 * What a device that never chose gets: LIGHT.
 *
 * ⚠ Not `system`, deliberately. The app was light for everybody until this setting existed, and a
 * phone already in dark mode would otherwise change appearance on the next deploy without anybody
 * having asked for it. Following the phone is one tap away.
 */
export const DEFAULT_APPEARANCE: Appearance = "light";

/**
 * The words on the account sheet's three options.
 *
 * A function, not a constant table: `t()` must run at render time, or the label is frozen in
 * whatever language was active when this module was imported.
 */
export function appearanceLabel(appearance: Appearance): string {
    switch (appearance) {
        case "dark":
            return t("mobileSheetAppearanceDark", "Dark");
        case "system":
            return t("mobileSheetAppearanceSystem", "Match device");
        default:
            return t("mobileSheetAppearanceLight", "Light");
    }
}

/** Whatever localStorage held, as a choice. Anything unrecognised is the default, never an error. */
export function parseAppearance(raw: unknown): Appearance {
    return (APPEARANCES as readonly unknown[]).includes(raw) ? (raw as Appearance) : DEFAULT_APPEARANCE;
}

/** The theme to draw for a choice, given whether the phone itself is in dark mode. */
export function themeFor(appearance: Appearance, deviceIsDark: boolean): Theme {
    if (appearance === "system") return deviceIsDark ? "dark" : "light";

    return appearance;
}
